import _extends from '../../../node_modules/@babel/runtime/helpers/extends.js';
import _slicedToArray from '../../../node_modules/@babel/runtime/helpers/slicedToArray.js';
import _objectWithoutProperties from '../../../node_modules/@babel/runtime/helpers/objectWithoutProperties.js';
import _defineProperty from '../../../node_modules/@babel/runtime/helpers/defineProperty.js';
import { forwardRef, useMemo, useRef, useEffect } from 'react';
import 'prop-types';
import merge from 'deepmerge';
import { jsx } from '@emotion/core';
import modalStyleObject from '../../styles/modal.js';
import { container } from '../../styles/grid/container.js';
import noop from '../../utils/noop.js';
import useClickOutside from '../../utils/useClickOutside.js';
import useElementRef from '../../utils/useElementRef.js';
import useEscape from '../../utils/useEscape.js';
import { ModalContext } from './ModalContext.js';
import { dlsWhite } from '../../styles/colorsDls/dlsWhite.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

var styleObject = _objectSpread(_objectSpread({}, modalStyleObject.modalScreen), modalStyleObject.modal);

var dialogStyleObject = {
  '&:focus': {
    outline: "dashed 1px ".concat(dlsWhite.color),
    outlineOffset: '3px',
    webkitTextDecoration: 'none',
    textDecoration: 'none'
  }
};

var modalWrapperStyleObject = _objectSpread(_objectSpread({}, container), {}, {
  width: '100%'
});

var Modal = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var theme = _ref.theme,
      onClose = _ref.onClose,
      children = _ref.children,
      modalDialogRef = _ref.modalDialogRef,
      otherProps = _objectWithoutProperties(_ref, ["theme", "onClose", "children", "modalDialogRef"]);

  var overlayCss = useMemo(function () {
    return merge.all([styleObject, theme]);
  }, [theme]);

  var _useElementRef = useElementRef(modalDialogRef),
      _useElementRef2 = _slicedToArray(_useElementRef, 2),
      dialogRef = _useElementRef2[0],
      dialogElement = _useElementRef2[1];

  var previouslyFocusedElement = useRef(undefined);

  var handleBlur = function handleBlur(event) {
    if (dialogElement && !dialogElement.contains(event.relatedTarget)) {
      event.stopPropagation();
      dialogElement.focus();
    }
  };

  useEffect(function () {
    var focusModalContent = function focusModalContent() {
      if (dialogElement) {
        dialogElement.focus();
      }
    };

    previouslyFocusedElement.current = document.activeElement;
    focusModalContent();
    return function () {
      /* istanbul ignore next */
      if (previouslyFocusedElement.current) {
        previouslyFocusedElement.current.focus();
      }
    };
  }, [dialogElement]);
  useClickOutside(null, onClose, dialogElement);
  useEscape(onClose);
  return jsx(ModalContext.Provider, {
    value: {
      onClose: onClose
    }
  }, jsx("div", _extends({
    css: overlayCss
  }, otherProps, {
    ref: ref
  }), jsx("div", {
    css: modalWrapperStyleObject
  }, jsx("div", {
    css: dialogStyleObject,
    role: "dialog",
    ref: dialogRef // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    ,
    tabIndex: "0",
    onBlur: handleBlur
  }, children))));
});
Modal.displayName = 'Modal';
Modal.defaultProps = {
  onClose: noop,
  theme: {},
  modalDialogRef: null
};

export default Modal;
